import {
    NO_PATTERN_BUSINESS_LOGIC,
    Price,
} from '@vfde-brix/ws10/core';
import {
    createTariffCards,
    ITariffCardBusinessLogic,
    TariffCards,
} from '@vfde-brix/ws10/tariff-card';
import {
    AppFlow,
    StateProps,
} from 'Container/App/interface';
import {
    getDataVolumeWithOrWithoutDiscounts,
    getFlattenedPrices,
    getMonthlyPriceWithOrWithoutDiscounts,
} from '@vfde-sails/glados-v2';
import {
    BUTTON_COLOR_PRIMARY,
    BUTTON_COLOR_TERTIARY,
} from '@vfde-brix/ws10/button';
import { ADDITIONAL_PAGE_OPTIONS } from '@vfde-sails/constants';
import {
    goToBasket,
    goToDeviceSelection,
} from 'Container/App/slice';
import { startAppListening } from 'Container/App/listener';
import {
    selectCustomerConfig,
    selectHasError,
    selectRedPlusOffers,
} from 'Container/App/selectors';
import {
    TARIFF_CARD_CONTAINER_ID,
    APP_CONTAINER_ID,
} from 'Container/App/constants';
import { useDispatch } from '../store';

const mountTariffCard = (): TariffCards | null => {
    const tariffCardContainer = document.getElementById(TARIFF_CARD_CONTAINER_ID);

    const tariffCards = tariffCardContainer && createTariffCards(tariffCardContainer, NO_PATTERN_BUSINESS_LOGIC);

    startAppListening({
        predicate: (_action, currentState, previousState) =>
            selectRedPlusOffers(currentState) !== selectRedPlusOffers(previousState),
        effect: (_action, listenerApi) => {
            const redPlusOffers = selectRedPlusOffers(listenerApi.getState());
            const customerConfig = selectCustomerConfig(listenerApi.getState());

            tariffCards && updateTariffCards(tariffCards, { customerConfig, redPlusOffers });
        },
    });

    startAppListening({
        predicate: (_action, currentState, previousState) =>
            selectHasError(currentState) !== selectHasError(previousState),
        effect: (_action, listenerApi) => {
            const hasError = selectHasError(listenerApi.getState());

            tariffCards?.toggleAncestor(hasError, `#${APP_CONTAINER_ID}`);
        },
    });

    return tariffCards;
};

/**
 * Update tariff cards with API information
 */
export const updateTariffCards = (tariffCards: TariffCards, state: Pick<StateProps, 'redPlusOffers' | 'customerConfig'>) => {
    const dispatch = useDispatch();
    const items = tariffCards.getProperties().items || [];
    const onButtonsClick: ITariffCardBusinessLogic['onButtonsClick'] = {};

    for (const item of items) {
        const matchingOffer = state.redPlusOffers.find(offer => offer.virtualItemId === item.stdProductId);

        if (!matchingOffer) {
            continue;
        }

        const dataVolume = getDataVolumeWithOrWithoutDiscounts(
            matchingOffer.dataVolume.withoutDiscounts,
            matchingOffer.dataVolume.withDiscounts,
        );

        const prices = getFlattenedPrices(matchingOffer.prices, false);
        const priceToPay = getMonthlyPriceWithOrWithoutDiscounts(
            prices.monthlyTariffPriceWithoutDiscounts,
            prices.monthlyTariffPriceWithDiscounts,
        );

        const strikePrice = prices.monthlyTariffPriceWithoutDiscounts;

        item.stdHeadline = (dataVolume?.unlimited ? item.stdHeadline : `${dataVolume?.value.toLocaleString('de')} ${dataVolume?.unit}`);
        item.stdHeadlineTopSmall = matchingOffer.tariffName;
        item.containerPromoPrice = {
            ...item.containerPromoPrice,
            price: Price.fromNumber(priceToPay.gross),
            ...strikePrice !== priceToPay && {
                strikePrice: Price.fromNumber(strikePrice.gross),
            },
        };

        onButtonsClick[matchingOffer.virtualItemId] = {
            [BUTTON_COLOR_PRIMARY]: () => dispatch(goToBasket(matchingOffer.virtualItemId, state.customerConfig.flow === AppFlow.Vvl
                ? true
                : undefined,
            )),
            [BUTTON_COLOR_TERTIARY]: () => state.customerConfig.flow === AppFlow.Vvl
                ? dispatch(goToBasket(matchingOffer.virtualItemId, false))
                : dispatch(goToDeviceSelection(matchingOffer.virtualItemId)),
        };

        for (const [index, button] of item.containerButtons.entries()) {
            switch (index) {
                case 0:
                    // Basket button
                    button.stdContent = window[ADDITIONAL_PAGE_OPTIONS][`primary_button_label_${matchingOffer.virtualItemId}`].trim();
                    break;
                case 1:
                    // Device selection
                    button.stdContent = window[ADDITIONAL_PAGE_OPTIONS][`secondary_button_label_${matchingOffer.virtualItemId}`].trim();
                    break;
            }
        }
    }

    tariffCards.update({ items, business: { onButtonsClick } });
};

export default mountTariffCard;
